import moment from 'moment'
import { RootState } from '.'
import type { AnyTicketSizesAction } from '../actions/ticketSizes'
import type { TicketSize } from '../types'
import { ArrayUtils } from '../utils'

const initialState = {
    ticketSizes: [] as TicketSize[],
    error: undefined as string | undefined,
    isWorking: false,
    selectedTicketSize: {
        data: undefined as TicketSize | undefined,
        error: undefined as string | undefined,
        isWorking: false,
    },
}

export type TicketSizesState = typeof initialState

type RawTicketSize = TicketSize

const toStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value.map((v) => String(v).trim()).filter(Boolean)
    }

    if (typeof value === 'string') {
        return value.split(',').map((v) => v.trim()).filter(Boolean)
    }

    return []
}

function formatTicketSize(rawTicketSize: RawTicketSize): TicketSize {
    return {
        ...rawTicketSize,
        start_date: moment(rawTicketSize.start_date, 'YYYY-MM-DD').toDate(),
        end_date: moment(rawTicketSize.end_date, 'YYYY-MM-DD').toDate(),
        storecodes: toStringArray(rawTicketSize.storecodes),
        skus: toStringArray(rawTicketSize.skus),
    }
}

function ticketSizesReducer(state: TicketSizesState = initialState, { type, payload }: AnyTicketSizesAction) {
    switch (type) {
        case 'TICKET_SIZES_FETCH##REQUEST':
            return {
                ...state,
                isWorking: true,
                error: undefined,
                ticketSizes: initialState.ticketSizes,
            }
        case 'TICKET_SIZES_FETCH##SUCCESS':
            return {
                ...state,
                ticketSizes: payload.map(formatTicketSize),
                isWorking: false,
                error: undefined,
            }
        case 'TICKET_SIZES_FETCH##FAILURE':
            return {
                ...state,
                isWorking: false,
                error: payload.message,
            }
        case 'TICKET_SIZE_ID_FETCH##REQUEST':
            return {
                ...state,
                selectedTicketSize: {
                    data: undefined,
                    isWorking: true,
                    error: undefined,
                },
            }
        case 'TICKET_SIZE_ID_FETCH##SUCCESS':
            return {
                ...state,
                selectedTicketSize: {
                    data: formatTicketSize(payload),
                    isWorking: false,
                    error: undefined,
                },
            }
        case 'TICKET_SIZE_ID_FETCH##FAILURE':
            return {
                ...state,
                selectedTicketSize: {
                    data: undefined,
                    isWorking: false,
                    error: payload.message,
                },
            }
        case 'TICKET_SIZE_DELETE##SUCCESS': {
            const index = state.ticketSizes.findIndex(ticketSize => ticketSize.id === payload)
            const updatedTicketSizes = ArrayUtils.removeItem(state.ticketSizes, index)
            let { selectedTicketSize } = state

            if (selectedTicketSize.data?.id === payload) {
                selectedTicketSize = initialState.selectedTicketSize
            }

            return {
                ...state,
                ticketSizes: updatedTicketSizes,
                selectedTicketSize,
            }
        }
        case 'TICKET_SIZE_UPDATE##SUCCESS': {
            const index = state.ticketSizes.findIndex(ticketSize => ticketSize.id === payload.id)
            const updatedTicketSizes = ArrayUtils.insertItem(
                state.ticketSizes,
                index,
                formatTicketSize(payload),
            )

            return {
                ...state,
                ticketSizes: updatedTicketSizes,
            }
        }
        case 'TICKET_SIZE_CREATE##SUCCESS': {
            return {
                ...state,
                ticketSizes: [
                    ...state.ticketSizes,
                    formatTicketSize(payload),
                ],
            }
        }
        default:
            return state
    }
}

function isWorking(state: RootState): boolean {
    return state.ticketSizes.isWorking
}

function selectTicketSizes(state: RootState): TicketSize[] {
    return state.ticketSizes.ticketSizes
}

function isLoadingSelectedTicketSize(state: RootState): boolean {
    return state.ticketSizes.selectedTicketSize.isWorking
}

function selectedTicketSize(state: RootState): TicketSize | undefined {
    return state.ticketSizes.selectedTicketSize.data
}

export default ticketSizesReducer

export {
    isLoadingSelectedTicketSize,
    isWorking,
    selectTicketSizes,
    selectedTicketSize,
}
